import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    Switch,
    message,
    Tooltip,
    Card,
    Row,
    Col,
    Statistic,
    Alert,  // ⭐ 추가
    Tabs,
    DatePicker
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    KeyOutlined,
    UserOutlined,
    TeamOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CalendarOutlined,
} from '@ant-design/icons';
import { userAPI, departmentAPI, attendanceAPI } from '../../services/api';
import dayjs from 'dayjs';

const { Option } = Select;

const UserManagementPage = () => {
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [filters, setFilters] = useState({});
    const [form] = Form.useForm();
    const [passwordForm] = Form.useForm();
    
    // 다중 선택 관련 state
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [bulkDeleteModalVisible, setBulkDeleteModalVisible] = useState(false);

    useEffect(() => {
        fetchUsers();
        fetchDepartments();
    }, []);

    const fetchUsers = async (filterParams = {}) => {
        setLoading(true);
        try {
            const response = await userAPI.getAll({ ...filters, ...filterParams });
            setUsers(response.data.users || []);
        } catch (error) {
            message.error('사용자 목록 조회 실패');
            console.error('Fetch users error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await departmentAPI.getAll();
            setDepartments(response.data.departments || []);
        } catch (error) {
            console.error('Fetch departments error:', error);
        }
    };

    const handleCreate = () => {
        setEditingUser(null);
        form.resetFields();
        // ⭐ 기본값만 설정
        form.setFieldsValue({
            role: 'user',
            is_active: true,
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingUser(record);

        // ⭐ roles 파싱 (안전하게)
        let rolesArray = [];
        if (record.roles) {
            if (Array.isArray(record.roles)) {
                rolesArray = record.roles;
            } else if (typeof record.roles === 'string') {
                try {
                    const parsed = JSON.parse(record.roles);
                    rolesArray = Array.isArray(parsed) ? parsed : [];
                } catch (e) {
                    console.error('roles 파싱 에러:', e);
                    rolesArray = [];
                }
            }
        }

        form.setFieldsValue({
            employee_id: record.employee_id,
            name: record.name,
            email: record.email,
            department_id: record.department_id,
            position: record.position,
            role: 'user', // ⭐ 기본 역할 사원으로 고정
            roles: rolesArray.filter(role => role !== 'user'), // ⭐ roles에서 user 제외
            phone: record.phone,
            bank_name: record.bank_name,
            bank_account: record.bank_account,
            account_holder: record.account_holder,
            is_active: record.is_active,
            hire_date: record.hire_date ? dayjs(record.hire_date) : null,
        });
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            console.log('🔵 Form values:', values);  // ⭐ 디버깅용

            // ⭐ roles를 JSON.stringify 하지 않고 그대로 전송, hire_date 포맷팅
            const submitData = {
                ...values,
                roles: values.roles || [],  // 배열 그대로 전송
                hire_date: values.hire_date ? values.hire_date.format('YYYY-MM-DD') : null,
            };

            console.log('🔵 Submit data:', submitData);  // ⭐ 디버깅용

            if (editingUser) {
                // 수정 시 employee_id 제외
                delete submitData.employee_id;
                delete submitData.password;

                await userAPI.update(editingUser.id, submitData);
                message.success('사용자 정보가 수정되었습니다');
            } else {
                const response = await userAPI.create(submitData);
                const newUser = response.data.user;
                message.success(`사용자가 생성되었습니다 (사번: ${newUser.employee_id})`);
            }

            setModalVisible(false);
            form.resetFields();
            fetchUsers();
        } catch (error) {
            console.error('Submit error:', error);  // ⭐ 에러 로그
            if (error.errorFields) {
                message.error('필수 항목을 입력해주세요');
            } else {
                message.error(error.response?.data?.message || '처리 실패');
            }
        }
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: '사용자 비활성화',
            content: `${record.name}(${record.employee_id}) 사용자를 비활성화하시겠습니까?`,
            okText: '비활성화',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await userAPI.delete(record.id);
                    message.success('사용자가 비활성화되었습니다');
                    fetchUsers();
                } catch (error) {
                    message.error(error.response?.data?.message || '비활성화 실패');
                }
            },
        });
    };

    const handleResetPassword = (record) => {
        setSelectedUser(record);
        passwordForm.resetFields();
        setPasswordModalVisible(true);
    };

    const handlePasswordReset = async () => {
        try {
            const values = await passwordForm.validateFields();

            await userAPI.resetPassword(selectedUser.id, {
                new_password: values.new_password,
            });

            message.success('비밀번호가 초기화되었습니다');
            setPasswordModalVisible(false);
            passwordForm.resetFields();
            setSelectedUser(null);
        } catch (error) {
            if (error.errorFields) {
                message.error('필수 항목을 입력해주세요');
            } else {
                message.error(error.response?.data?.message || '비밀번호 초기화 실패');
            }
        }
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        if (!value) delete newFilters[key];
        setFilters(newFilters);
        fetchUsers(newFilters);
        // 필터 변경 시 선택 초기화
        setSelectedRowKeys([]);
        setSelectedUsers([]);
    };

    // 다중 선택 관련 함수들
    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
        const newSelectedUsers = users.filter(user => newSelectedRowKeys.includes(user.id));
        setSelectedUsers(newSelectedUsers);
    };

    const handleBulkDelete = () => {
        if (selectedUsers.length === 0) {
            message.warning('삭제할 사용자를 선택하세요');
            return;
        }
        setBulkDeleteModalVisible(true);
    };

    const confirmBulkDelete = async () => {
        try {
            const userIds = selectedRowKeys;
            
            // 백엔드 API 호출 (각 사용자별로 개별 삭제 호출)
            const deletePromises = userIds.map(id => userAPI.delete(id));
            await Promise.all(deletePromises);

            message.success(`${selectedUsers.length}명의 사용자가 비활성화되었습니다`);
            
            // 상태 초기화 및 새로고침
            setSelectedRowKeys([]);
            setSelectedUsers([]);
            setBulkDeleteModalVisible(false);
            fetchUsers();
        } catch (error) {
            message.error(error.response?.data?.message || '일괄 비활성화 실패');
        }
    };

    const clearSelection = () => {
        setSelectedRowKeys([]);
        setSelectedUsers([]);
    };

    const roleOptions = [
        { label: '사원', value: 'user' },
        { label: 'PM', value: 'pm' },
        { label: '부서장', value: 'manager' },
        { label: '재무담당', value: 'finance' },
        { label: '관리자', value: 'admin' },
    ];

    // 추가 역할에서는 사원을 제외한 나머지 역할만 표시
    const additionalRoleOptions = roleOptions.filter(role => role.value !== 'user');

    const roleColors = {
        admin: 'red',
        finance: 'blue',
        manager: 'green',
        pm: 'cyan',
        user: 'default',
    };

    const columns = [
        {
            title: '사번',
            dataIndex: 'employee_id',
            key: 'employee_id',
            width: 100,
        },
        {
            title: '이름',
            dataIndex: 'name',
            key: 'name',
            width: 100,
        },
        {
            title: '이메일',
            dataIndex: 'email',
            key: 'email',
            ellipsis: true,
        },
        {
            title: '부서',
            dataIndex: ['department', 'name'],
            key: 'department',
            width: 120,
        },
        {
            title: '직급',
            dataIndex: 'position',
            key: 'position',
            width: 100,
        },
        {
            title: '역할',
            key: 'roles',
            width: 200,
            render: (_, record) => {
                let rolesArray = [];

                // ⭐ roles 파싱 (안전하게)
                if (record.roles) {
                    if (Array.isArray(record.roles)) {
                        // 이미 배열인 경우
                        rolesArray = [...record.roles];  // ⭐ 복사본 생성
                    } else if (typeof record.roles === 'string') {
                        // JSON 문자열인 경우
                        try {
                            const parsed = JSON.parse(record.roles);
                            rolesArray = Array.isArray(parsed) ? parsed : [];
                        } catch (e) {
                            console.error('roles 파싱 에러:', e, record.roles);
                            rolesArray = [];
                        }
                    }
                }

                // ⭐ role도 추가 (중복 제거)
                if (record.role && !rolesArray.includes(record.role)) {
                    rolesArray = [...rolesArray, record.role];  // ⭐ 새 배열 생성
                }

                return (
                    <Space size={4} wrap>
                        {rolesArray.map((role) => (
                            <Tag key={role} color={roleColors[role] || 'default'}>
                                {roleOptions.find((r) => r.value === role)?.label || role}
                            </Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: '상태',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 80,
            align: 'center',
            render: (is_active) =>
                is_active ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">
                        활성
                    </Tag>
                ) : (
                    <Tag icon={<CloseCircleOutlined />} color="default">
                        비활성
                    </Tag>
                ),
        },
        {
            title: '작업',
            key: 'action',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="수정">
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Tooltip title="비밀번호 초기화">
                        <Button
                            type="link"
                            size="small"
                            icon={<KeyOutlined />}
                            onClick={() => handleResetPassword(record)}
                        />
                    </Tooltip>
                    <Tooltip title="비활성화">
                        <Button
                            type="link"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDelete(record)}
                            disabled={!record.is_active}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const statistics = {
        total: users.length,
        active: users.filter((u) => u.is_active).length,
        inactive: users.filter((u) => !u.is_active).length,
        admin: users.filter((u) => {
            let roles = [];

            // ⭐ 안전한 roles 파싱
            if (u.roles) {
                if (Array.isArray(u.roles)) {
                    roles = u.roles;
                } else if (typeof u.roles === 'string') {
                    try {
                        const parsed = JSON.parse(u.roles);
                        roles = Array.isArray(parsed) ? parsed : [];
                    } catch (e) {
                        roles = [];
                    }
                }
            }

            return roles.includes('admin') || u.role === 'admin';
        }).length,
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2>사용자 관리</h2>
                <Space>
                    {selectedUsers.length > 0 && (
                        <>
                            <Button 
                                danger 
                                icon={<DeleteOutlined />} 
                                onClick={handleBulkDelete}
                            >
                                선택 삭제 ({selectedUsers.length}명)
                            </Button>
                            <Button onClick={clearSelection}>
                                선택 취소
                            </Button>
                        </>
                    )}
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                        사용자 추가
                    </Button>
                </Space>
            </div>

            {/* 통계 */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="전체 사용자"
                            value={statistics.total}
                            prefix={<UserOutlined />}
                            suffix="명"
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="활성 사용자"
                            value={statistics.active}
                            prefix={<CheckCircleOutlined />}
                            suffix="명"
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="비활성 사용자"
                            value={statistics.inactive}
                            prefix={<CloseCircleOutlined />}
                            suffix="명"
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="관리자"
                            value={statistics.admin}
                            prefix={<TeamOutlined />}
                            suffix="명"
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 필터 */}
            <Card style={{ marginBottom: 16 }}>
                <Space size="middle" wrap>
                    <Select
                        placeholder="부서 선택"
                        style={{ width: 150 }}
                        allowClear
                        onChange={(value) => handleFilterChange('department_id', value)}
                    >
                        {departments.map((dept) => (
                            <Option key={dept.id} value={dept.id}>
                                {dept.name}
                            </Option>
                        ))}
                    </Select>
                    <Select
                        placeholder="역할 선택"
                        style={{ width: 150 }}
                        allowClear
                        onChange={(value) => handleFilterChange('role', value)}
                    >
                        {roleOptions.map((role) => (
                            <Option key={role.value} value={role.value}>
                                {role.label}
                            </Option>
                        ))}
                    </Select>
                    <Select
                        placeholder="상태"
                        style={{ width: 120 }}
                        allowClear
                        onChange={(value) => handleFilterChange('is_active', value)}
                    >
                        <Option value="true">활성</Option>
                        <Option value="false">비활성</Option>
                    </Select>
                    <Input.Search
                        placeholder="이름, 사번, 이메일 검색"
                        style={{ width: 250 }}
                        allowClear
                        onSearch={(value) => handleFilterChange('search', value)}
                    />
                </Space>
            </Card>

            {/* 사용자 테이블 */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={users}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1200 }}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: onSelectChange,
                        getCheckboxProps: (record) => ({
                            disabled: !record.is_active, // 비활성 사용자는 선택 불가
                        }),
                    }}
                />
            </Card>

            {/* 사용자 생성/수정 모달 */}
            <Modal
                title={editingUser ? '사용자 수정' : '사용자 생성'}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                width={700}
                okText={editingUser ? '수정' : '생성'}
                cancelText="취소"
            >
                <Form form={form} layout="vertical">
                    {/* ⭐ 생성 시 안내 메시지 추가 */}
                    {!editingUser && (
                        <Alert
                            message="초기 비밀번호 안내"
                            description="초기 비밀번호는 'user1234!'로 자동 설정됩니다. 사용자는 최초 로그인 시 비밀번호를 변경해야 합니다."
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Row gutter={16}>
                        {/* 사번 필드 - 수정 시에만 표시 */}
                        {editingUser && (
                            <Col span={12}>
                                <Form.Item name="employee_id" label="사번">
                                    <Input disabled />
                                </Form.Item>
                            </Col>
                        )}
                        <Col span={editingUser ? 12 : 24}>
                            <Form.Item
                                name="name"
                                label="이름"
                                rules={[{ required: true, message: '이름을 입력하세요' }]}
                            >
                                <Input placeholder="홍길동" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="email"
                        label="이메일"
                        rules={[
                            { required: true, message: '이메일을 입력하세요' },
                            { type: 'email', message: '올바른 이메일 형식이 아닙니다' },
                        ]}
                    >
                        <Input placeholder="user@example.com" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="department_id" label="부서">
                                <Select placeholder="부서 선택" allowClear>
                                    {departments.map((dept) => (
                                        <Option key={dept.id} value={dept.id}>
                                            {dept.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="position" label="직급">
                                <Input placeholder="사원, 대리, 과장..." />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* ⭐⭐⭐ 역할 필드 - 생성/수정 모두 표시 */}
                    <Form.Item
                        name="role"
                        label="기본 역할"
                        rules={[{ required: true, message: '기본 역할을 선택하세요' }]}
                    >
                        <Select placeholder="기본 역할 선택" disabled value="user">
                            <Option value="user">사원</Option>
                        </Select>
                    </Form.Item>

                    {/* ⭐⭐⭐ 추가 역할 - 생성/수정 모두 표시 (사원 제외) */}
                    <Form.Item
                        name="roles"
                        label="추가 역할"
                        extra="기본 역할(사원) 외에 추가로 부여할 역할을 선택하세요"
                    >
                        <Select
                            mode="multiple"
                            placeholder="추가 역할 선택 (선택사항)"
                            allowClear
                        >
                            {additionalRoleOptions.map((role) => (
                                <Option key={role.value} value={role.value}>
                                    {role.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="phone" label="전화번호">
                        <Input placeholder="010-0000-0000" />
                    </Form.Item>

                    <Form.Item 
                        name="hire_date" 
                        label="입사일"
                    >
                        <DatePicker 
                            placeholder="입사일 선택" 
                            style={{ width: '100%' }}
                            format="YYYY-MM-DD"
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="bank_name" label="은행">
                                <Input placeholder="신한은행" />
                            </Form.Item>
                        </Col>
                        <Col span={10}>
                            <Form.Item name="bank_account" label="계좌번호">
                                <Input placeholder="110-123-456789" />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="account_holder" label="예금주">
                                <Input placeholder="홍길동" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="is_active" label="활성 상태" valuePropName="checked">
                        <Switch checkedChildren="활성" unCheckedChildren="비활성" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 비밀번호 초기화 모달 */}
            <Modal
                title="비밀번호 초기화"
                open={passwordModalVisible}
                onOk={handlePasswordReset}
                onCancel={() => {
                    setPasswordModalVisible(false);
                    passwordForm.resetFields();
                    setSelectedUser(null);
                }}
                okText="초기화"
                cancelText="취소"
            >
                {selectedUser && (
                    <div style={{ marginBottom: 16 }}>
                        <strong>{selectedUser.name}</strong> ({selectedUser.employee_id}) 사용자의 비밀번호를
                        초기화합니다.
                    </div>
                )}
                <Form form={passwordForm} layout="vertical">
                    <Form.Item
                        name="new_password"
                        label="새 비밀번호"
                        rules={[
                            { required: true, message: '새 비밀번호를 입력하세요' },
                            { min: 6, message: '비밀번호는 6자 이상이어야 합니다' },
                        ]}
                    >
                        <Input.Password placeholder="새 비밀번호 (6자 이상)" />
                    </Form.Item>
                    <Form.Item
                        name="confirm_password"
                        label="비밀번호 확인"
                        dependencies={['new_password']}
                        rules={[
                            { required: true, message: '비밀번호를 다시 입력하세요' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('new_password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('비밀번호가 일치하지 않습니다'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="비밀번호 확인" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 일괄 삭제 확인 모달 */}
            <Modal
                title="사용자 일괄 비활성화"
                open={bulkDeleteModalVisible}
                onOk={confirmBulkDelete}
                onCancel={() => setBulkDeleteModalVisible(false)}
                okText="비활성화"
                okType="danger"
                cancelText="취소"
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <strong>{selectedUsers.length}명</strong>의 사용자를 비활성화하시겠습니까?
                </div>
                
                <div style={{ 
                    maxHeight: 200, 
                    overflowY: 'auto', 
                    border: '1px solid #f0f0f0', 
                    borderRadius: 6,
                    padding: 12,
                    backgroundColor: '#fafafa'
                }}>
                    {selectedUsers.map((user) => (
                        <div key={user.id} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            padding: '4px 0',
                            borderBottom: '1px solid #f0f0f0'
                        }}>
                            <Space>
                                <strong>{user.name}</strong>
                                <span style={{ color: '#666' }}>({user.employee_id})</span>
                                <Tag color="blue">{user.department?.name || '부서 없음'}</Tag>
                            </Space>
                        </div>
                    ))}
                </div>

                <Alert
                    message="주의사항"
                    description="비활성화된 사용자는 로그인할 수 없으며, 시스템에서 제외됩니다. 이 작업은 되돌릴 수 있습니다."
                    type="warning"
                    showIcon
                    style={{ marginTop: 16 }}
                />
            </Modal>
        </div>
    );
};

export default UserManagementPage;